import { create } from "zustand";
import { subscribeWithSelector } from "zustand/middleware";
import { Calls, CallSessionConnectionState, Media } from "connectycube/types";

export enum ActiveMedia {
  USER = 0,
  DISPLAY = 1,
}

export enum CallDataType {
  ON_CALL = "onCall",
  ON_STOP_CALLING = "onStopCalling",
  ON_ACCEPT = "onAccept",
  ON_REJECT = "onReject",
  ON_STOP = "onStop",
  ON_NOT_ANSWER = "onNotAnswer",
  ON_CONNECTION_STATE = "onConnectionState",
}

export type CallData = {
  userID?: number;
  userInfo?: Calls.UserInfo;
  connectionState?: CallSessionConnectionState;
};

export interface CallsStoreSessionState {
  activeCall: Calls.Session | null;
  incomingCall: Calls.Session | null;
}
export interface CallsStoreSessionDataState {
  deviceChangeEvent: Event | null;
  incomingCallCallData: CallData | null;
  incomingCallStopData: CallData | null;
  activeCallAcceptData: CallData | null;
  activeCallRejectData: CallData | null;
  activeCallStopData: CallData | null;
  activeCallNotAnswerData: CallData | null;
  activeCallConnectionStateData: CallData | null;
}
export interface CallsStoreMediaState {
  localStream: MediaStream | null;
  remoteStreams: { [key: number]: MediaStream };
  userMediaParams: Media.UserParams;
  displayMediaParams: Media.DisplayParams;
  activeMedia: ActiveMedia;
}
export interface CallsStoreState extends CallsStoreSessionState, CallsStoreSessionDataState, CallsStoreMediaState {
  participantsIDs: Set<number>;
  hasActiveListeners: boolean;
}

export interface CallsStoreSessionActions {
  setActiveCall: (session: Calls.Session) => void;
  resetActiveCall: () => void;
  setIncomingCall: (session: Calls.Session) => void;
  resetIncomingCall: () => void;
}
export interface CallsStoreSessionDataActions {
  setDeviceChangeEvent: (deviceChangeEvent: Event) => void;
  upsertSessionData: (type: CallDataType, data: CallData) => void;
  resetSessionData: (type?: CallDataType) => void;
}
export interface CallsStoreMediaActions {
  setLocalStream: (stream: MediaStream) => void;
  resetLocalStream: () => void;
  addRemoteStream: (key: number, stream: MediaStream) => void;
  removeRemoteStream: (key: number) => void;
  resetRemoteStreams: () => void;
  saveUserMediaParams: (params: Media.UserParams) => void;
  resetUserMediaParams: () => void;
  saveDisplayMediaParams: (params: Media.DisplayParams) => void;
  resetDisplayMediaParams: () => void;
  saveActiveMedia: (activeMedia: ActiveMedia) => void;
}
export interface CallsStoreActions
  extends CallsStoreSessionActions,
    CallsStoreSessionDataActions,
    CallsStoreMediaActions {
  setParticipantsIDs: (participantsIDs: number[]) => void;
  participantDidEnter: (id: number) => void;
  participantDidLeave: (id: number) => void;
  activateCallsListeners: () => void;
  resetStore: () => void;
}

interface CallsStore extends CallsStoreState, CallsStoreActions {}

const initialSessionState = {
  activeCall: null,
  incomingCall: null,
};
const initialSessionDataState = {
  incomingCallCallData: null,
  incomingCallStopData: null,
  activeCallAcceptData: null,
  activeCallRejectData: null,
  activeCallStopData: null,
  activeCallNotAnswerData: null,
  activeCallConnectionStateData: null,
  deviceChangeEvent: null,
};
const initialMediaState = {
  localStream: null,
  remoteStreams: {},
  userMediaParams: { video: true, audio: true },
  displayMediaParams: { video: true, audio: false },
  activeMedia: ActiveMedia.USER,
};
const initialState: CallsStoreState = {
  ...initialSessionState,
  ...initialSessionDataState,
  ...initialMediaState,
  participantsIDs: new Set(),
  hasActiveListeners: false,
};

const useCallsStore = create<CallsStore>()(
  subscribeWithSelector((set, get) => ({
    ...initialState,
    // active call session
    setActiveCall: (activeCall?: Calls.Session) => set({ activeCall }),
    resetActiveCall: () => set({ activeCall: initialState.activeCall }),
    // incoming call session (waiting)
    setIncomingCall: (incomingCall?: Calls.Session) => set({ incomingCall }),
    resetIncomingCall: () => set({ incomingCall: initialState.incomingCall }),
    // data from listeners
    setDeviceChangeEvent: (deviceChangeEvent: Event) => set({ deviceChangeEvent }),
    upsertSessionData: (type: CallDataType, data: CallData) => {
      switch (type) {
        case CallDataType.ON_CALL:
          set({ incomingCallCallData: data });
          break;
        case CallDataType.ON_STOP_CALLING:
          set({ incomingCallStopData: data });
          break;
        case CallDataType.ON_ACCEPT:
          set({ activeCallAcceptData: data });
          break;
        case CallDataType.ON_REJECT:
          set({ activeCallRejectData: data });
          break;
        case CallDataType.ON_STOP:
          set({ activeCallStopData: data });
          break;
        case CallDataType.ON_NOT_ANSWER:
          set({ activeCallNotAnswerData: data });
          break;
        case CallDataType.ON_CONNECTION_STATE:
          set({ activeCallConnectionStateData: data });
          break;
        default:
          break;
      }
    },
    resetSessionData: (type?: CallDataType) => {
      switch (type) {
        case CallDataType.ON_CALL:
          set({ incomingCallCallData: initialSessionDataState.incomingCallCallData });
          break;
        case CallDataType.ON_STOP_CALLING:
          set({ incomingCallStopData: initialSessionDataState.incomingCallStopData });
          break;
        case CallDataType.ON_ACCEPT:
          set({ activeCallAcceptData: initialSessionDataState.activeCallAcceptData });
          break;
        case CallDataType.ON_REJECT:
          set({ activeCallRejectData: initialSessionDataState.activeCallRejectData });
          break;
        case CallDataType.ON_STOP:
          set({ activeCallStopData: initialSessionDataState.activeCallStopData });
          break;
        case CallDataType.ON_NOT_ANSWER:
          set({ activeCallNotAnswerData: initialSessionDataState.activeCallNotAnswerData });
          break;
        case CallDataType.ON_CONNECTION_STATE:
          set({ activeCallConnectionStateData: initialSessionDataState.activeCallConnectionStateData });
          break;
        default:
          set(initialSessionDataState);
          break;
      }
    },
    // local stream
    setLocalStream: (localStream: MediaStream) => set({ localStream }),
    resetLocalStream: () => set({ localStream: initialState.localStream }),
    // remote stream
    addRemoteStream: (key: number, stream: MediaStream) =>
      set({ remoteStreams: { ...get().remoteStreams, [key]: stream } }),
    removeRemoteStream: (key: number) => {
      const remoteStreams = { ...get().remoteStreams };
      delete remoteStreams[key];
      set({ remoteStreams });
    },
    resetRemoteStreams: () => set({ remoteStreams: initialState.remoteStreams }),
    // getUserMedia/getDisplayMedia params controls
    saveUserMediaParams: (userMediaParams: Media.UserParams) => set({ userMediaParams }),
    resetUserMediaParams: () => set({ userMediaParams: initialState.userMediaParams }),
    saveDisplayMediaParams: (displayMediaParams: Media.DisplayParams) => set({ displayMediaParams }),
    resetDisplayMediaParams: () => set({ displayMediaParams: initialState.displayMediaParams }),
    saveActiveMedia: (activeMedia: ActiveMedia) => set({ activeMedia }),
    // pending/active call users ids
    setParticipantsIDs: (participantsIDs: number[]) => set({ participantsIDs: new Set(participantsIDs) }),
    participantDidEnter: (id: number) => {
      const participantsIDs = new Set([...get().participantsIDs]);
      participantsIDs.add(id);
      set({ participantsIDs });
    },
    participantDidLeave: (id: number) => {
      const participantsIDs = new Set([...get().participantsIDs]);
      participantsIDs.delete(id);
      set({ participantsIDs });
    },
    // setup calls listeners
    activateCallsListeners: () => set({ hasActiveListeners: true }),
    // clean store
    resetStore: () => set({ ...initialState, hasActiveListeners: get().hasActiveListeners }),
  })),
);

export default useCallsStore;
